import React, { useState, useMemo } from 'react';
import { getModuleStats, formatDuration, getStatus } from '../utils/testUtils';
import TestCaseDrawer from './TestCaseDrawer';
// import { useTheme } from '../App';
import { FiCheckCircle, FiXCircle, FiMinusCircle, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { Box, Flex, Input,Table, Thead, Tbody, Tr, Th, Td, useDisclosure, useColorModeValue, Icon } from '@chakra-ui/react';

function Breadcrumbs({ moduleName }) {
  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      <a href="/">Dashboard</a>
      <span>/</span>
      <a href="/history">Test Run History</a>
      <span>/</span>
      <span aria-current="page">{moduleName}</span>
    </nav>
  );
}

function ProgressRing({ percent, total }) {
  const radius = 50;
  const stroke = 10;
  const normalizedRadius = radius - stroke / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percent / 100) * circumference;
  return (
    <div className="progress-ring" style={{ position: 'relative', width: 110, height: 110 }}>
      <svg width={110} height={110}>
        <circle cx={55} cy={55} r={normalizedRadius} fill="var(--color-border-light)" />
        <circle
          cx={55}
          cy={55}
          r={normalizedRadius}
          fill="none"
          stroke="var(--color-status-success)"
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform="rotate(-90 55 55)"
          style={{ transition: 'stroke-dashoffset 0.6s' }}
        />
      </svg>
      <span className="progress-ring-text" style={{ color: 'var(--color-text-dark)'}}>{total}</span>
    </div>
  );
}

function SummaryPanel({ moduleName, passed, failed, total, percent, suiteName, onCollapse, collapsed }) {
  return (
    <aside className={`summary-panel${collapsed ? ' collapsed' : ''}`}> 
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-dark)' }}>{moduleName}</div>
        {onCollapse && (
          <button aria-label={collapsed ? 'Expand summary' : 'Collapse summary'} onClick={onCollapse} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--color-text-light)' }}>{collapsed ? <FiChevronRight /> : <FiChevronLeft />}</button>
        )}
      </div>
      <ProgressRing percent={percent} total={total} />
      <div style={{ marginBottom: 12, color: 'var(--color-text-main)' }}>
        <span className="status-badge passed"><FiCheckCircle /> Passed</span>: {passed} ({percent}%)
      </div>
      <div style={{ marginBottom: 12, color: 'var(--color-text-main)' }}>
        <span className="status-badge failed"><FiXCircle /> Failed</span>: {failed} ({total > 0 ? Math.round((failed/total)*100) : 0}%)
      </div>
      <div>
        <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--color-text-dark)' }}>Test Suite</div>
        <div style={{ border: '1px solid var(--color-border-light)', borderRadius: 8, padding: '6px 12px', fontSize: 15, fontWeight: 500, marginBottom: 6, background: 'var(--color-background-main)', color: 'var(--color-text-dark)' }}>{suiteName} <span style={{ background: 'var(--color-accent-bg)', color: 'var(--color-text-dark)', borderRadius: 8, padding: '2px 8px', marginLeft: 8, fontSize: 13 }}>{total}</span></div>
      </div>
    </aside>
  );
}

function TestCaseList({ cases, onSelect, selectedIdx, onCopy, onPrev, onNext }) {
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBg = useColorModeValue('green.50', 'gray.700');
  const tableHeaderColor = useColorModeValue('green.700', 'green.200');
  const tableRowBgEven = useColorModeValue('white', 'gray.800');
  const tableRowBgOdd = useColorModeValue('gray.50', 'gray.700');
  const successColor = useColorModeValue('green.500', 'green.300');
  const failureColor = useColorModeValue('red.500', 'red.300');
  const skippedColor = useColorModeValue('yellow.600', 'yellow.300');

  const selectedRowBgLight = useColorModeValue('blue.100', 'blue.700');
  const selectedRowBgHoverLight = useColorModeValue('blue.200', 'blue.600');
  const defaultRowBgHoverLight = useColorModeValue('gray.100', 'gray.700');
  const selectedRowBorderColor = useColorModeValue('blue.500', 'blue.300');

  return (
    <Box overflowX="auto" borderRadius="lg" boxShadow="md" borderWidth={1} borderColor={cardBorder} bg={cardBg} w="100%">
      <Table variant="simple" size="md">
        <Thead bg={tableHeaderBg}>
          <Tr>
            <Th color={tableHeaderColor} py={3}>Status</Th>
            <Th color={tableHeaderColor} py={3}>Test Name</Th>
            <Th color={tableHeaderColor} isNumeric py={3}>Duration</Th>
            <Th color={tableHeaderColor} py={3}>Error/Failure</Th>
          </Tr>
        </Thead>
        <Tbody>
          {cases.length === 0 ? (
            <Tr>
              <Td colSpan={4} textAlign="center" py={8} color={textColor}>No test cases found matching your criteria.</Td>
            </Tr>
          ) : cases.map((tc, i) => {
            const status = getStatus(tc);
            const isSelected = selectedIdx === i;
            const rowBg = isSelected ? selectedRowBgLight : (i % 2 === 0 ? tableRowBgEven : tableRowBgOdd);
            const statusColor = status === 'passed' ? successColor : status === 'failed' ? failureColor : skippedColor;

            return (
              <Tr
                key={i}
                bg={rowBg}
                _hover={{ bg: isSelected ? selectedRowBgHoverLight : defaultRowBgHoverLight }}
                cursor="pointer"
                onClick={() => onSelect(i)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') onSelect(i);
                  if (e.key === 'ArrowDown' && i < cases.length - 1) onNext(i);
                  if (e.key === 'ArrowUp' && i > 0) onPrev(i);
                }}
                tabIndex={0}
                aria-selected={isSelected}
                borderColor={isSelected ? selectedRowBorderColor : 'transparent'}
                borderWidth={isSelected ? 1 : 0}
              >
                <Td color={statusColor} fontWeight="bold">
                  <Flex align="center" gap={2}>
                    {status === 'passed' ? <Icon as={FiCheckCircle} /> : status === 'failed' ? <Icon as={FiXCircle} /> : <Icon as={FiMinusCircle} />}
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </Flex>
                </Td>
                <Td color={textColor}>{tc.name}</Td>
                <Td isNumeric color={textColor}>{formatDuration(tc.time)}</Td>
                <Td color={failureColor} fontSize="sm">
                  {tc.failure?.message || tc.error?.message || tc.failure || tc.error}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
}

function Toast({ message, show }) {
  return <div className={`toast${show ? ' show' : ''}`}>{message}</div>;
}

function TestRunDetail({ moduleName, cases }) {
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);
  // const { dark } = useTheme();
  const { passed, failed, total, percent } = useMemo(() => getModuleStats(cases), [cases]);
  const filteredCases = useMemo(() => cases.filter(tc => tc.name.toLowerCase().includes(search.toLowerCase())), [cases, search]);
  const suiteName = cases[0]?.classname || moduleName;

  // State for the TestCaseDrawer
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();
  const [selectedTestCase, setSelectedTestCase] = useState(null);

  function handleCopy(text) {
    navigator.clipboard.writeText(text);
    setToast('Copied!');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 1200);
  }
  function handleSelect(idx) {
    setSelectedIdx(idx);
    setSelectedTestCase(filteredCases[idx]);
    onDrawerOpen();
  }
  function handlePrev(idx) {
    if (idx > 0) handleSelect(idx - 1);
  }
  function handleNext(idx) {
    if (idx < filteredCases.length - 1) handleSelect(idx + 1);
  }

  // Collapsible summary for mobile
  const [collapsed, setCollapsed] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 900;

  const containerBg = useColorModeValue('gray.50', 'gray.900'); // Example background for the whole page

  return (
    <Box className="test-run-details-container" p={{ base: 4, md: 8 }} w="100%" bg={containerBg} minH="100vh">
      <Flex direction={{ base: 'column', md: 'row' }} gap={{ base: 6, md: 8 }} w="100%">
        <Box flex={1} display="flex" flexDirection="column">
          <Breadcrumbs moduleName={moduleName} />
          <Flex mb={6} align="center" gap={3}>
            <Input
              placeholder="Search Test Cases"
              value={search}
              onChange={e => setSearch(e.target.value)}
              size="md"
              borderRadius="md"
              flex={{ base: 1, sm: 'auto' }}
              width={{ base: '100%', sm: 'auto' }}
            />
          </Flex>
          <TestCaseList
            cases={filteredCases}
            onSelect={handleSelect}
            selectedIdx={selectedIdx}
            onCopy={handleCopy}
            onPrev={handlePrev}
            onNext={handleNext}
          />
        </Box>
        <SummaryPanel
          moduleName={moduleName}
          passed={passed}
          failed={failed}
          total={total}
          percent={percent}
          suiteName={suiteName}
          onCollapse={isMobile ? () => setCollapsed(c => !c) : undefined}
          collapsed={collapsed}
        />
      </Flex>
      <TestCaseDrawer
        open={isDrawerOpen}
        onClose={() => {
          onDrawerClose();
          setSelectedTestCase(null);
        }}
        testcase={selectedTestCase}
      />
      <Toast message={toast} show={showToast} />
    </Box>
  );
}

export default TestRunDetail; 